/**
 * Iter209am — Pannello generazione/gestione inviti operatori.
 *
 * Usato in:
 *   - Users.jsx (admin HouseTag): invita operator roles (tech/site/desk/
 *     accounting) + supervisor flag.
 *   - TeamPage.jsx (Pro Master): invita sub-user professional (team).
 *
 * Il componente adatta il set di ruoli in base a `variant`:
 *   - "admin"  → ADMIN_ROLES + supervisor toggle.
 *   - "team"   → solo `professional` (sub-user del pro master).
 *
 * Flusso:
 *   1. POST /admin/invites → nuovo invito, mostra `invite_url` + copia.
 *   2. GET  /admin/invites → lista con stato pending/accepted/expired/
 *      revoked.
 *   3. DELETE /admin/invites/{token} → revoca invito pending.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  LinkSimple, Copy, ShareNetwork, Trash, CheckCircle, Clock, X as XIcon, Prohibit,
} from "@phosphor-icons/react";

// Iter209au — Etichette derivate dal ROLE_LABELS canonico
// (`@/lib/api`) invece di essere hardcoded qui.
const ROLE_OPTIONS_ADMIN = [
  { value: "tech_operator",       label: ROLE_LABELS.tech_operator },
  { value: "site_operator",       label: ROLE_LABELS.site_operator },
  { value: "desk_operator",       label: ROLE_LABELS.desk_operator },
  { value: "accounting_operator", label: ROLE_LABELS.accounting_operator },
];
const ROLE_OPTIONS_TEAM = [
  { value: "professional", label: "Membro del team (sub-user)" },
];

const STATUS_META = {
  pending:  { label: "In attesa",  color: "bg-amber-100 text-amber-800",  icon: Clock },
  accepted: { label: "Accettato",  color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  expired:  { label: "Scaduto",    color: "bg-slate-200 text-slate-700",  icon: XIcon },
  revoked:  { label: "Revocato",   color: "bg-red-100 text-red-700",      icon: Prohibit },
};

export default function InviteLinkPanel({ variant = "admin", testId = "invite-link-panel" }) {
  const ROLE_OPTIONS = variant === "admin" ? ROLE_OPTIONS_ADMIN : ROLE_OPTIONS_TEAM;
  const [role, setRole] = useState(ROLE_OPTIONS[0].value);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter209bn — Casella email da CREARE al volo su Zoho. L'admin
  // digita solo lo username (es. "a.basso"), il suffisso @housetag.it
  // è mostrato accanto. Obbligatorio per variant admin.
  const [mailboxUsername, setMailboxUsername] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invites");
      setList(r.data?.invites || []);
    } catch (e) {
      // 403 se ruolo non autorizzato — mostra lista vuota
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Iter209bn — Rimosso il fetch delle caselle "unassigned":
  // ora la casella viene CREATA al momento dell'invito.

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    // Iter209bn — Validazione username obbligatorio per admin
    if (variant === "admin") {
      const uname = mailboxUsername.trim().toLowerCase();
      if (!uname) {
        toast.error("Inserisci lo username della casella email");
        return;
      }
      if (!/^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/.test(uname)) {
        toast.error("Username non valido: usa lettere minuscole, numeri, punti, trattini");
        return;
      }
    }
    setGenerating(true);
    try {
      const payload = {
        role,
        is_supervisor: variant === "admin" ? !!isSupervisor : false,
      };
      if (variant === "admin") {
        payload.mailbox_username = mailboxUsername.trim().toLowerCase();
      }
      const r = await api.post("/admin/invites", payload);
      setLastInvite(r.data);
      setMailboxUsername("");
      toast.success("Link invito generato e casella creata");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore generazione invito";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch {
      // Fallback per browser senza clipboard API
      const t = document.createElement("textarea");
      t.value = url;
      document.body.appendChild(t);
      t.select();
      try {
        document.execCommand("copy");
        toast.success("Link copiato");
      } catch {
        toast.error("Impossibile copiare — copia manualmente");
      } finally {
        document.body.removeChild(t);
      }
    }
  };

  const share = async (url, orgName) => {
    if (typeof navigator?.share === "function") {
      try {
        await navigator.share({
          title: `Invito Housetag`,
          text: `Ti invito a unirti a ${orgName || "Housetag"} come operatore.`,
          url,
        });
      } catch {}
    } else {
      copy(url);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revocare questo invito? Il link non funzionerà più.")) return;
    try {
      await api.delete(`/admin/invites/${token}`);
      toast.success("Invito revocato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore revoca");
    }
  };

  const buildInviteUrl = (token) => {
    if (typeof window === "undefined") return `/join/${token}`;
    return `${window.location.origin}/join/${token}`;
  };

  return (
    <div className="kpi-card space-y-4" data-testid={testId}>
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
          <LinkSimple size={18} weight="bold" className="text-purple-600" /> Invita operatore
        </h3>
        <p className="text-xs text-slate-500">
          Genera un link condivisibile via WhatsApp, SMS, email o qualsiasi altro canale.
          Il link scade dopo 72 ore e può essere usato una sola volta.
        </p>
      </div>

      {/* Form generazione */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <div>
            <Label>Ruolo</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`invite-role-opt-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {variant === "admin" && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-700">Supervisore</div>
                <div className="text-[11px] text-slate-500">Accesso esteso alle pratiche</div>
              </div>
              <Switch
                checked={isSupervisor}
                onCheckedChange={setIsSupervisor}
                data-testid="invite-supervisor-toggle"
              />
            </div>
          )}
        </div>
        {/* Iter209bn — Casella email da CREARE al volo (obbligatoria).
            L'admin digita lo username; il suffisso @housetag.it è
            mostrato accanto. La casella viene provisionata su Zoho al
            momento della generazione dell'invito. Se lo username esiste
            già → errore 409 "Casella già esistente". */}
        {variant === "admin" && (
          <div>
            <Label>
              Username casella email <span className="text-red-500">*</span>
            </Label>
            <div className="flex items-stretch rounded-md border overflow-hidden bg-white">
              <input
                type="text"
                value={mailboxUsername}
                onChange={(e) => setMailboxUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
                placeholder="es. a.basso"
                autoComplete="off"
                data-testid="invite-mailbox-username"
                className="flex-1 px-3 py-2 text-sm outline-none"
                required
              />
              <span className="px-3 py-2 text-sm bg-slate-100 text-slate-700 border-l flex items-center font-mono">
                @housetag.it
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              La casella verrà creata su Zoho al momento della generazione del link invito. Se esiste già, la generazione viene bloccata.
            </p>
          </div>
        )}
        <Button
          onClick={generate}
          disabled={generating}
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          data-testid="invite-generate-btn"
        >
          {generating ? "Generazione…" : "Genera link invito"}
        </Button>

        {lastInvite && (
          <div className="mt-3 rounded-lg border-2 border-purple-200 bg-purple-50 p-3 space-y-2" data-testid="invite-last-generated">
            <div className="text-xs text-purple-700 font-medium">
              Link generato — condividilo prima della scadenza ({new Date(lastInvite.expires_at).toLocaleString("it-IT")})
            </div>
            {lastInvite.email_account_email && (
              <div
                className="text-[11px] text-purple-800 bg-white/60 rounded px-2 py-1 border border-purple-200"
                data-testid="invite-last-mailbox"
              >
                Casella pre-assegnata: <span className="font-mono">{lastInvite.email_account_email}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={lastInvite.invite_url || buildInviteUrl(lastInvite.token)}
                className="flex-1 text-xs font-mono px-2 py-1.5 bg-white border border-purple-300 rounded-md truncate"
                data-testid="invite-last-url"
                onFocus={(e) => e.target.select()}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(lastInvite.invite_url || buildInviteUrl(lastInvite.token))}
                data-testid="invite-copy-btn"
              >
                <Copy size={14} /> Copia
              </Button>
              {typeof navigator?.share === "function" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => share(lastInvite.invite_url || buildInviteUrl(lastInvite.token))}
                  data-testid="invite-share-btn"
                >
                  <ShareNetwork size={14} /> Condividi
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lista inviti */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between">
          <h4 className="font-display text-sm font-semibold text-slate-900">Inviti recenti</h4>
          <Button size="sm" variant="ghost" onClick={load} data-testid="invite-refresh-btn">
            Aggiorna
          </Button>
        </div>
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Caricamento…</div>
        ) : list.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center" data-testid="invite-empty">
            Nessun invito. Genera il primo con il form sopra.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2">Ruolo</th>
                <th className="text-left px-3 py-2">Casella</th>
                <th className="text-left px-3 py-2">Stato</th>
                <th className="text-left px-3 py-2">Creato</th>
                <th className="text-left px-3 py-2">Scade</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {list.map((inv) => {
                const meta = STATUS_META[inv.status] || STATUS_META.pending;
                const StatusIcon = meta.icon;
                const isPending = inv.status === "pending";
                const inviteUrl = buildInviteUrl(inv.token);
                const roleLabel = ROLE_OPTIONS.find((r) => r.value === inv.role)?.label
                  || ROLE_OPTIONS_ADMIN.find((r) => r.value === inv.role)?.label
                  || inv.role;
                return (
                  <tr key={inv.token} className="border-b last:border-0" data-testid={`invite-row-${inv.token}`}>
                    <td className="px-3 py-2">
                      {roleLabel}
                      {inv.is_supervisor && (
                        <span className="ml-1 text-[10px] uppercase font-bold text-purple-700">SUP</span>
                      )}
                    </td>
                    {/* Iter209as — Casella pre-assegnata (o "—" se
                        nessuna). Mostra sia il caso pending sia
                        l'accettato dopo l'auto-link. */}
                    <td className="px-3 py-2 text-xs">
                      {inv.email_account_email ? (
                        <span
                          className="font-mono text-slate-700"
                          data-testid={`invite-mailbox-cell-${inv.token}`}
                        >
                          {inv.email_account_email}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${meta.color}`} data-testid={`invite-status-${inv.status}`}>
                        <StatusIcon size={11} weight="bold" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(inv.created_at)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(inv.expires_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {isPending && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => copy(inviteUrl)} data-testid={`invite-copy-row-${inv.token}`}>
                              <Copy size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800" onClick={() => revoke(inv.token)} data-testid={`invite-revoke-${inv.token}`}>
                              <Trash size={13} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
